import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import React from 'react';

import { StatusChip } from '@/components/Cards/Chips/StatusChip';
import Modal from '@/components/Modals/Modal';

import type { Order } from '@/types/plugins/mlFulfill';

import { getStatusOrderBg, getStatusOrderColor, getStatusOrderLabel } from '../../constants';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

interface PairTitleProps {
  title: string;
  value?: string;
  isBold?: boolean;
  isGreen?: boolean;
  isTitleGreen?: boolean;
  children?: React.ReactNode;
}

const PairTitle = ({ title, value, isBold, isGreen, isTitleGreen, children }: PairTitleProps) => {
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1} textAlign="center">
      <Typography
        variant="body1"
        fontWeight={theme.typography.fontWeightMedium}
        sx={{ color: isTitleGreen ? theme.palette.primary.main : 'inherit' }}
      >
        {title}
      </Typography>
      <Box display="flex" alignItems="center">
        {children ?? (
          <Typography
            variant="body1"
            sx={{
              fontWeight: isBold ? 'bold' : 'normal',
              whiteSpace: 'normal',
              wordBreak: 'break-all',
              color: isGreen ? theme.palette.primary.main : theme.palette.grey[800],
              textAlign: 'center',
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const OrderDetailsModal = ({ isOpen, onClose, order }: Props) => {
  const theme = useTheme();

  const cellStyleHeader = {
    padding: '12px',
    fontWeight: 600,
    color: theme.palette.secondary.main,
    whiteSpace: 'nowrap' as const,
  };

  const cellStyle = {
    padding: '10px',
    color: '#444',
    backgroundColor: '#fafafa',
    whiteSpace: 'nowrap' as const,
  };

  return (
    <Modal isOpen={isOpen} title="Detalle de orden" onClose={onClose} maxWidth="xl">
      {order ? (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Box display="flex" gap={2} width="100%" alignItems="stretch">
            <Box flex={3}>
              <Typography
                color="text.secondary"
                fontWeight={700}
                display="block"
                textAlign="center"
                mb={1}
              >
                Mercado Libre
              </Typography>
              <Grid container spacing={2} justifyContent="center">
                <Grid size={{ xxs: 12, sm: 4 }}>
                  <PairTitle
                    title="Fecha de compra"
                    value={
                      order.createdAt
                        ? format(new Date(order.createdAt), 'dd/MM/yyyy')
                        : format(new Date(), 'dd/MM/yyyy')
                    }
                  />
                </Grid>
                <Grid size={{ xxs: 12, sm: 4 }}>
                  <PairTitle title="ID orden" value={order.orderId} isTitleGreen isBold />
                </Grid>
                <Grid size={{ xxs: 12, sm: 4 }}>
                  <PairTitle title="Estatus">
                    <StatusChip
                      status={getStatusOrderLabel(order.statusOrder)}
                      color={getStatusOrderColor(order.statusOrder)}
                      bg={getStatusOrderBg(order.statusOrder)}
                    />
                  </PairTitle>
                </Grid>
                {(order.orderData?.logisticMode || order.orderData?.logisticType) && (
                  <>
                    <Grid size={{ xxs: 12, sm: 6 }}>
                      <PairTitle
                        title="Modo logístico"
                        value={order.orderData.logisticMode ?? '-'}
                      />
                    </Grid>
                    <Grid size={{ xxs: 12, sm: 6 }}>
                      <PairTitle
                        title="Tipo logístico"
                        value={order.orderData.logisticType ?? '-'}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box flex={1} display="flex" flexDirection="column" alignItems="center">
              <Typography
                color="text.secondary"
                fontWeight={700}
                display="block"
                textAlign="center"
                mb={1}
              >
                99minutos
              </Typography>
              <PairTitle
                title="Nº de seguimiento"
                value={
                  order.trackingId && order.trackingId.length > 0 ? order.trackingId : 'Sin asignar'
                }
              />
            </Box>
          </Box>

          <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

          <Grid container spacing={2} width={1} justifyContent="center">
            <Grid size={{ xxs: 12, md: 6 }}>
              <PairTitle
                title="Dirección de origen"
                value={
                  order.orderData?.originAddressData?.address
                    ? `${order.orderData.originAddressData.address}, CP: ${order.orderData.originAddressData.zipCode || 'N/A'}`
                    : 'No disponible'
                }
              />
            </Grid>
            <Grid size={{ xxs: 12, md: 6 }}>
              <PairTitle
                title="Dirección de destino"
                value={
                  order.orderData?.destinationAddressData?.address
                    ? `${order.orderData.destinationAddressData.address}, CP: ${order.orderData.destinationAddressData.zipCode ?? 'N/A'}`
                    : 'No disponible'
                }
              />
            </Grid>
          </Grid>

          <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />

          {order.orderData?.items?.length > 0 && (
            <>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                color={theme.palette.primary.main}
                align="center"
                mt={2}
              >
                Producto(s)
              </Typography>

              <Box mt={2} sx={{ width: '100%', overflowX: 'auto', paddingBottom: '1rem' }}>
                <Box
                  component="table"
                  sx={{
                    minWidth: 500,
                    width: '100%',
                    borderCollapse: 'collapse',
                    textAlign: 'center',
                    fontSize: '0.95rem',
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f9f9f9' }}>
                      <th style={cellStyleHeader}>Nombre del producto</th>
                      <th style={cellStyleHeader}>SKU</th>
                      <th style={cellStyleHeader}>Cantidad</th>
                      <th style={cellStyleHeader}>Peso (g)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.orderData.items.map((item, index) => (
                      <tr key={index} style={{ borderTop: '1px solid #eee' }}>
                        <td style={cellStyle}>{item.name}</td>
                        <td style={cellStyle}>{item.sku}</td>
                        <td style={cellStyle}>{item.quantity}</td>
                        <td style={cellStyle}>{item.weight}</td>
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>

              <Divider variant="fullWidth" sx={{ width: '100%', margin: '1.25rem 0' }} />
            </>
          )}

          <Grid container spacing={2} width={1} justifyContent="center" alignItems="center">
            <Grid size={{ xxs: 12, md: 6 }}>
              <PairTitle title="Moneda" value={order.orderData?.currency ?? ''} isBold isGreen />
            </Grid>
            <Grid size={{ xxs: 12, md: 6 }}>
              <PairTitle
                title="Costo de orden"
                value={`$ ${
                  parseFloat(order.orderData?.amount ?? '0') >= 1000
                    ? parseFloat(order.orderData?.amount ?? '0').toLocaleString('en-US')
                    : (order.orderData?.amount ?? 0)
                }`}
                isBold
                isGreen
              />
            </Grid>
          </Grid>
        </Box>
      ) : null}
    </Modal>
  );
};

export default OrderDetailsModal;
